/**
 * Gate 2 — Step 1: Defining the Execution Scope.
 *
 * Picks which Gate 1 test suites (or metrics) this run covers and declares the
 * build each is tested against. Selection and build declarations live in the
 * parent page so they survive stepping forward to Assign & Execute and back.
 */
import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import StickyPageHeaderWithBack from "../../components/layout/StickyPageHeaderWithBack";
import { ReactComponent as CheckBoxUncheckedIcon } from "../../asserts/logo/Check_box.svg";
import {
  SECTION_SUBTITLE_SX,
} from "./gate2Tokens";
import ApiConnectionCard from "./ApiConnectionCard";
import VectorDbConnectionCard from "./VectorDbConnectionCard";
import ExecutionScopeItemDetailView from "./ExecutionScopeItemDetailView";
import SelectDropdownTheme from "../../components/SelectDropdownTheme";
import AddTestCaseDialog from "../gate1/AddTestCaseDialog";
import { CONNECTORS } from "../../utils/metricConnectors";
import {
  SCOPE_TABLE_WRAP_SX,
  SCOPE_TABLE_SX,
  GATE2_CHECKBOX_FOCUS_SX,
  SCOPE_TABLE_CHECKBOX_CELL_SX,
  SCOPE_TABLE_METRIC_NAME_CELL_SX,
  SCOPE_TABLE_METRIC_NAME_HEAD_SX,
  SCOPE_TABLE_METRIC_NAME_VALUE_SX,
  SCOPE_TABLE_THRESHOLD_HEAD_SX,
  SCOPE_TABLE_THRESHOLD_VALUE_SX,
  SCOPE_TABLE_TEST_CASES_HEAD_SX,
  SCOPE_TABLE_TEST_CASES_VALUE_SX,
  SCOPE_TABLE_ACTIONS_HEAD_SX,
  SCOPE_VIEW_DETAILS_LINK_SX,
  SCOPE_METRICS_CARD_SX,
  SCOPE_METRICS_BLOCKED_TEXT_SX,
  SCOPE_METRICS_BLOCKED_ROW_OPACITY,
  SCOPE_METRICS_TABLE_HEAD_CELL_SX,
  SCOPE_METRICS_TABLE_BODY_CELL_SX,
  SCOPE_TABLE_ACTIONS_CELL_SX,
  SCOPE_TABLE_THRESHOLD_CELL_SX,
  SCOPE_TABLE_TEST_CASES_CELL_SX,
  SCOPE_TABLE_ACTIONS_COL_SX,
} from "./gate2ScopeTableStyles";

const NOT_IMPL_CHIP_SX = {
  height: 22,
  borderRadius: "11px",
  px: "10px",
  fontFamily: "Lato, sans-serif",
  fontSize: 11,
  fontWeight: 700,
  bgcolor: "#F1F5F9",
  color: "#64748B",
};

function formatThresholdValue(threshold, unit) {
  if (threshold == null || Number.isNaN(Number(threshold))) return "—";
  const n = Number(threshold);
  if (unit === "fraction" || unit === "rate" || unit === "percent" || unit === "%") {
    const pct = n <= 1 && n > 0 && n < 1 ? n * 100 : n;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function formatExpectedThreshold(item, scopeMode) {
  if (scopeMode !== "metric") return "—";
  if (item?.threshold == null || Number.isNaN(Number(item.threshold))) return "—";
  const dir = item.direction || "gte";
  const prefix = dir === "lte" ? "≤" : dir === "lt" ? "<" : dir === "eq" ? "=" : dir === "gt" ? ">" : "≥";
  return `${prefix} ${formatThresholdValue(item.threshold, item.unit)}`;
}

/** Same unchecked/checked icons as Gate 1 Test Cases (`Check_box.svg`). */
const SCOPE_TABLE_CHECKBOX_SX = {
  ...GATE2_CHECKBOX_FOCUS_SX,
  p: 0,
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const SCOPE_CHECKBOX_ICON_PROPS = { style: { width: 18, height: 18, display: "block" } };

function ScopeCheckBoxCheckedIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path
        d="M5.25 9L7.75 11.5L12.75 6.5"
        stroke="#FFFFFF"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ScopeCheckBoxIndeterminateIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path d="M5.25 9H12.75" stroke="#FFFFFF" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ExecutionScopeCheckbox({ sx, ...props }) {
  return (
    <Checkbox
      size="small"
      disableRipple
      icon={<CheckBoxUncheckedIcon {...SCOPE_CHECKBOX_ICON_PROPS} />}
      checkedIcon={<ScopeCheckBoxCheckedIcon {...SCOPE_CHECKBOX_ICON_PROPS} />}
      indeterminateIcon={<ScopeCheckBoxIndeterminateIcon {...SCOPE_CHECKBOX_ICON_PROPS} />}
      sx={{ ...SCOPE_TABLE_CHECKBOX_SX, ...sx }}
      {...props}
    />
  );
}

export default function ExecutionScopeStep({
  showPageHeader = true,
  confirmationMode = false,
  onBack,
  items,
  scopeMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  builds,
  detailItemId,
  onOpenDetails,
  onCloseDetails,
  testCasesById,
  metrics = [],
  risks = [],
  running = false,
  vectorOptional = false,
  appEndpoint = null,
  requiredConnectors = [],
  apiForm,
  apiCredentials,
  onApiFormChange,
  onApiCredentialsChange,
  gate1ApiSource,
  onOpenGate1Api,
  vectorForm,
  vectorCredentials,
  onVectorFormChange,
  onVectorCredentialsChange,
  gate1Source,
  onOpenGate1Vector,
  projectId = null,
}) {
  const emptyLabel =
    scopeMode === "suite"
      ? "No test suites are available. Create one in Gate 1 before defining an execution scope."
      : "No metrics are available for this project.";
  const executableItems = items.filter((item) => item.executable !== false);
  const selectedCount = executableItems.filter((item) => selectedIds.has(item.id)).length;
  const allSelected = executableItems.length > 0 && selectedCount === executableItems.length;
  const someSelected = selectedCount > 0;
  const selectAllLabel = allSelected ? "Deselect all" : "Select all";
  const detailItem = detailItemId ? items.find((row) => row.id === detailItemId) : null;
  const [caseDetailId, setCaseDetailId] = useState("");

  const caseDetailTestCase = useMemo(() => {
    if (!caseDetailId || !testCasesById) return null;
    return testCasesById.get(caseDetailId) || testCasesById.get(String(caseDetailId)) || null;
  }, [caseDetailId, testCasesById]);

  const renderSelectAllCheckbox = (id) => (
    <ExecutionScopeCheckbox
      id={id}
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      onChange={onToggleSelectAll}
      disabled={running || !executableItems.length}
      inputProps={{ "aria-label": selectAllLabel }}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 0,
      }}
    >
      {showPageHeader ? (
        <StickyPageHeaderWithBack
          onBack={onBack}
          backAriaLabel="Back to test suites"
          title={confirmationMode ? "Review & Execute" : "Defining the Execution Scope"}
          subtitle={
            confirmationMode
              ? "Review your selected metrics below and proceed to execute the evaluation run."
              : "Select which approved Gate 1B Test Suites or Metrics apply to this run, then declare the exact build and environment they will be tested against."
          }
        />
      ) : null}

      <Box
        component="section"
        aria-labelledby="gate2-scope-metrics-heading"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
      {detailItem ? (
        <>
          <ExecutionScopeItemDetailView
            item={detailItem}
            scopeMode={scopeMode}
            onBack={() => {
              setCaseDetailId("");
              onCloseDetails();
            }}
            onViewCaseDetails={setCaseDetailId}
          />
          <SelectDropdownTheme>
            <AddTestCaseDialog
              open={Boolean(caseDetailTestCase)}
              onClose={() => setCaseDetailId("")}
              onSave={() => {}}
              metrics={metrics}
              risks={risks}
              editingTestCase={caseDetailTestCase}
              viewOnly
              projectId={projectId}
            />
          </SelectDropdownTheme>
        </>
      ) : (
      <>
      <Box
        sx={{
          p: "24px",
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Box sx={SCOPE_METRICS_CARD_SX}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              id="gate2-scope-metrics-heading"
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 20,
                fontWeight: 700,
                lineHeight: "normal",
                letterSpacing: 0,
                color: "#0F172A",
              }}
            >
              {confirmationMode ? "Confirmed Metrics" : "Select Metrics for This Run"}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "normal",
                letterSpacing: 0,
                color: "#475569",
                mt: 0.5,
              }}
            >
              {confirmationMode
                ? "These metrics will be evaluated against all associated test cases."
                : "Approved in Gate 1B. Select which metrics apply to this run and declare the build configuration."}
            </Typography>
          </Box>
        </Box>

          {!items.length ? (
            <Typography sx={{ ...SECTION_SUBTITLE_SX, mt: 0, py: 4, textAlign: "center" }}>
              {emptyLabel}
            </Typography>
          ) : (
            <TableContainer sx={SCOPE_TABLE_WRAP_SX}>
              <Table
                size="small"
                sx={SCOPE_TABLE_SX}
                aria-labelledby="gate2-scope-metrics-heading"
              >
                <colgroup>
                  {!confirmationMode ? <col style={{ width: 44 }} /> : null}
                  <col />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {!confirmationMode ? (
                      <TableCell
                        sx={{
                          ...SCOPE_METRICS_TABLE_HEAD_CELL_SX,
                          ...SCOPE_TABLE_CHECKBOX_CELL_SX,
                        }}
                      >
                        {renderSelectAllCheckbox("gate2-select-all-table")}
                      </TableCell>
                    ) : null}
                    <TableCell
                      sx={{
                        ...SCOPE_METRICS_TABLE_HEAD_CELL_SX,
                        ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                        ...SCOPE_TABLE_METRIC_NAME_HEAD_SX,
                      }}
                    >
                      {scopeMode === "suite" ? "Suite Name" : "Metric Name"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...SCOPE_METRICS_TABLE_HEAD_CELL_SX,
                        ...SCOPE_TABLE_THRESHOLD_HEAD_SX,
                        ...SCOPE_TABLE_THRESHOLD_CELL_SX,
                      }}
                    >
                      Expected Threshold
                    </TableCell>
                    <TableCell
                      sx={{
                        ...SCOPE_METRICS_TABLE_HEAD_CELL_SX,
                        ...SCOPE_TABLE_TEST_CASES_HEAD_SX,
                        ...SCOPE_TABLE_TEST_CASES_CELL_SX,
                      }}
                    >
                      Test Cases
                    </TableCell>
                    <TableCell
                      sx={{
                        ...SCOPE_METRICS_TABLE_HEAD_CELL_SX,
                        ...SCOPE_TABLE_ACTIONS_HEAD_SX,
                        ...SCOPE_TABLE_ACTIONS_COL_SX,
                        ...SCOPE_TABLE_ACTIONS_CELL_SX,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const blocked = item.executable === false;
                    return (
                    <TableRow
                      key={item.id}
                      hover={false}
                      selected={false}
                      sx={blocked ? { opacity: SCOPE_METRICS_BLOCKED_ROW_OPACITY } : undefined}
                    >
                      {!confirmationMode ? (
                        <TableCell sx={{ ...SCOPE_METRICS_TABLE_BODY_CELL_SX, ...SCOPE_TABLE_CHECKBOX_CELL_SX }}>
                          <Tooltip title={blocked ? "Not implementable" : ""}>
                            <span>
                              <ExecutionScopeCheckbox
                                checked={!blocked && selectedIds.has(item.id)}
                                disabled={running || blocked}
                                onChange={() => onToggleSelect(item.id)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      ) : null}
                      <TableCell
                        sx={{
                          ...SCOPE_METRICS_TABLE_BODY_CELL_SX,
                          ...SCOPE_TABLE_METRIC_NAME_CELL_SX,
                          ...SCOPE_TABLE_METRIC_NAME_VALUE_SX,
                          ...(blocked ? SCOPE_METRICS_BLOCKED_TEXT_SX : null),
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          {item.name}
                          {blocked ? (
                            <Chip size="small" label="Not implementable" sx={NOT_IMPL_CHIP_SX} />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          ...SCOPE_METRICS_TABLE_BODY_CELL_SX,
                          ...SCOPE_TABLE_THRESHOLD_VALUE_SX,
                          ...SCOPE_TABLE_THRESHOLD_CELL_SX,
                          ...(blocked ? SCOPE_METRICS_BLOCKED_TEXT_SX : null),
                        }}
                      >
                        {formatExpectedThreshold(item, scopeMode)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...SCOPE_METRICS_TABLE_BODY_CELL_SX,
                          ...SCOPE_TABLE_TEST_CASES_VALUE_SX,
                          ...SCOPE_TABLE_TEST_CASES_CELL_SX,
                          ...(blocked ? SCOPE_METRICS_BLOCKED_TEXT_SX : null),
                        }}
                      >
                        {item.cases.length} test case{item.cases.length === 1 ? "" : "s"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...SCOPE_METRICS_TABLE_BODY_CELL_SX,
                          ...SCOPE_TABLE_ACTIONS_COL_SX,
                          ...SCOPE_TABLE_ACTIONS_CELL_SX,
                        }}
                      >
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => onOpenDetails(item.id)}
                          sx={SCOPE_VIEW_DETAILS_LINK_SX}
                        >
                          View Details
                        </Link>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {!confirmationMode && appEndpoint && vectorOptional ? (
          <Box sx={SCOPE_METRICS_CARD_SX}>
            <Typography sx={{ ...SECTION_SUBTITLE_SX, mt: 0, fontWeight: 700, color: "#0F172A" }}>
              Application endpoint
            </Typography>
            <Typography sx={SECTION_SUBTITLE_SX}>
              Content-generator runs call the application under test. Vector search is optional and
              only used for context-backed metrics when Gate 1 has a verified vector store.
            </Typography>
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#0F172A", mt: 1.5 }}>
              {appEndpoint.available
                ? `${appEndpoint.baseUrl || ""}${appEndpoint.endpoint || ""}`
                : "No base URL and endpoint declared in Gate 1 readiness yet."}
            </Typography>
          </Box>
        ) : null}

        {!confirmationMode && requiredConnectors.includes(CONNECTORS.VECTOR_STORE) ? (
          <VectorDbConnectionCard
            form={vectorForm}
            credentials={vectorCredentials}
            onFormChange={onVectorFormChange}
            onCredentialsChange={onVectorCredentialsChange}
            disabled={running}
            gate1Source={gate1Source}
            onOpenGate1={onOpenGate1Vector}
          />
        ) : null}

        {!confirmationMode && requiredConnectors.includes(CONNECTORS.REST_API) ? (
          <ApiConnectionCard
            form={apiForm}
            credentials={apiCredentials}
            onFormChange={onApiFormChange}
            onCredentialsChange={onApiCredentialsChange}
            disabled={running}
            gate1Source={gate1ApiSource}
            onOpenGate1={onOpenGate1Api}
          />
        ) : null}

        {!confirmationMode && selectedIds.size > 0 && !requiredConnectors.length && !vectorOptional ? (
          <Box sx={SCOPE_METRICS_CARD_SX}>
            <Typography sx={{ ...SECTION_SUBTITLE_SX, mt: 0, textAlign: "center" }}>
              These metrics do not need a customer connection for this run.
            </Typography>
          </Box>
        ) : null}
      </Box>
      </>
      )}
      </Box>
    </Box>
  );
}
